/**
 * ConfirmDialog: yes/no confirmation over Modal (e.g. "Are you sure you want
 * to promote this user?"). `tone="danger"` gives the confirm button the
 * destructive variant and puts initial focus on Cancel. While `busy`, both
 * buttons are disabled and Escape / overlay clicks do nothing.
 */
import { useRef, type JSX, type ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  tone?: 'default' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** `data-testid` for the confirm button (e.g. `promote-confirm`). */
  confirmTestId?: string;
  /** `data-testid` for the Cancel button. */
  cancelTestId?: string;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  tone = 'default',
  busy = false,
  onConfirm,
  onCancel,
  confirmTestId,
  cancelTestId,
}: ConfirmDialogProps): JSX.Element {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const cancel = (): void => {
    if (!busy) onCancel();
  };

  return (
    <Modal
      open={open}
      onClose={cancel}
      title={title}
      size="sm"
      closeOnOverlay={!busy}
      initialFocusRef={tone === 'danger' ? cancelRef : confirmRef}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" onClick={cancel} disabled={busy} data-testid={cancelTestId}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => {
              if (!busy) onConfirm();
            }}
            disabled={busy}
            loading={busy}
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {typeof body === 'string' ? <p>{body}</p> : (body ?? null)}
    </Modal>
  );
}
